import api from './api';
import { ApiResponse } from '../types/api';
import type {
  CommentFilterParams,
  CommunityComment,
  CreateCommentDto,
  CreateCommentResult,
  EnrolledSubject,
  PaginatedComments,
} from '../types/community';

function unwrapPayload<T>(body: ApiResponse<unknown>): T | null {
  if (!body.success) return null;
  const message = body.message;
  const data = body.data;
  if (message && typeof message === 'object') return message as T;
  if (data && typeof data === 'object') return data as T;
  return null;
}

function normalizeReply(raw: Record<string, unknown>) {
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    text: String(raw.text ?? raw.Text ?? ''),
    repliedBy: String(raw.repliedBy ?? raw.RepliedBy ?? ''),
    replierName: (raw.replierName ?? raw.ReplierName ?? null) as string | null,
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
  };
}

function normalizeComment(raw: Record<string, unknown>): CommunityComment {
  const repliesRaw = (raw.replies ?? raw.Replies ?? []) as Record<string, unknown>[];
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    text: String(raw.text ?? raw.Text ?? ''),
    studentId: String(raw.studentId ?? raw.StudentId ?? ''),
    studentName: String(raw.studentName ?? raw.StudentName ?? ''),
    subjectId: (raw.subjectId ?? raw.SubjectId ?? null) as string | null,
    subjectName: (raw.subjectName ?? raw.SubjectName ?? null) as string | null,
    courseId: (raw.courseId ?? raw.CourseId ?? null) as string | null,
    courseName: (raw.courseName ?? raw.CourseName ?? null) as string | null,
    unitId: (raw.unitId ?? raw.UnitId ?? null) as number | null,
    unitName: (raw.unitName ?? raw.UnitName ?? null) as string | null,
    lessonId: (raw.lessonId ?? raw.LessonId ?? null) as number | null,
    lessonName: (raw.lessonName ?? raw.LessonName ?? null) as string | null,
    grade: (raw.grade ?? raw.Grade ?? null) as string | null,
    status: String(raw.status ?? raw.Status ?? 'Draft') as CommunityComment['status'],
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
    modifiedAt: String(raw.modifiedAt ?? raw.ModifiedAt ?? ''),
    replies: Array.isArray(repliesRaw) ? repliesRaw.map(normalizeReply) : [],
  };
}

function normalizePaginated(raw: Record<string, unknown>): PaginatedComments {
  const commentsRaw = (raw.comments ?? raw.Comments ?? []) as Record<string, unknown>[];
  return {
    comments: Array.isArray(commentsRaw) ? commentsRaw.map(normalizeComment) : [],
    totalCount: Number(raw.totalCount ?? raw.TotalCount ?? 0),
    page: Number(raw.page ?? raw.Page ?? 1),
    pageSize: Number(raw.pageSize ?? raw.PageSize ?? 20),
    totalPages: Number(raw.totalPages ?? raw.TotalPages ?? 0),
  };
}

function getErrorMessage(body: ApiResponse<unknown>, fallback: string): string {
  if (typeof body.message === 'string' && body.message) return body.message;
  if (typeof body.data === 'string' && body.data) return body.data;
  return fallback;
}

function normalizeSubject(raw: Record<string, unknown>): EnrolledSubject {
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    name: String(raw.name ?? raw.Name ?? ''),
    description: (raw.description ?? raw.Description ?? null) as string | null,
    code: raw.code != null || raw.Code != null ? String(raw.code ?? raw.Code) : undefined,
    gradeId: raw.gradeId != null || raw.GradeId != null ? String(raw.gradeId ?? raw.GradeId) : undefined,
    gradeName: (raw.gradeName ?? raw.GradeName ?? null) as string | null,
  };
}

function unwrapListPayload(body: ApiResponse<unknown>): Record<string, unknown>[] | null {
  const raw = body as ApiResponse<unknown> & {
    Success?: boolean;
    Message?: unknown;
    Data?: unknown;
  };
  const success = raw.success ?? raw.Success;
  if (!success) return null;
  const message = raw.message ?? raw.Message;
  const data = raw.data ?? raw.Data;
  if (Array.isArray(message)) return message as Record<string, unknown>[];
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  return null;
}

export const communityService = {
  async getEnrolledSubjects() {
    try {
      const response = await api.get<ApiResponse<unknown>>('/api/Student/Student-Enrolled-Subjects');
      const body = response.data;
      const list = unwrapListPayload(body);
      if (!list) {
        return {
          success: false as const,
          error: getErrorMessage(body, 'تعذر تحميل المواد المسجّلة'),
        };
      }
      const subjects = list.map(normalizeSubject).filter((s) => s.id && s.name);
      return { success: true as const, data: subjects };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const body = err.response?.data;
      return {
        success: false as const,
        error: body
          ? getErrorMessage(body, 'تعذر تحميل المواد المسجّلة')
          : 'تعذر تحميل المواد المسجّلة',
      };
    }
  },

  async getPublishedComments(params: CommentFilterParams = {}) {
    try {
      const response = await api.get<ApiResponse<unknown>>('/api/community/comments', { params });
      const body = response.data;
      const payload = unwrapPayload<Record<string, unknown>>(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'تعذر تحميل التعليقات') };
      }
      return { success: true as const, data: normalizePaginated(payload) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const body = err.response?.data;
      return {
        success: false as const,
        error: body ? getErrorMessage(body, 'تعذر تحميل التعليقات') : 'تعذر تحميل التعليقات',
      };
    }
  },

  async getMyComments(params: CommentFilterParams = {}) {
    try {
      const response = await api.get<ApiResponse<unknown>>('/api/community/my-comments', { params });
      const body = response.data;
      const payload = unwrapPayload<Record<string, unknown>>(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'تعذر تحميل تعليقاتك') };
      }
      return { success: true as const, data: normalizePaginated(payload) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const body = err.response?.data;
      return {
        success: false as const,
        error: body ? getErrorMessage(body, 'تعذر تحميل تعليقاتك') : 'تعذر تحميل تعليقاتك',
      };
    }
  },

  async createComment(dto: CreateCommentDto) {
    try {
      const response = await api.post<ApiResponse<unknown>>('/api/community/comments', dto);
      const body = response.data;
      if (!body.success) {
        return { success: false as const, error: getErrorMessage(body, 'تعذر إرسال التعليق') };
      }
      const payload = unwrapPayload<Record<string, unknown>>(body);
      const commentId = String(payload?.commentId ?? payload?.CommentId ?? '');
      return { success: true as const, data: { commentId } as CreateCommentResult };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const body = err.response?.data;
      return {
        success: false as const,
        error: body ? getErrorMessage(body, 'تعذر إرسال التعليق') : 'تعذر إرسال التعليق',
      };
    }
  },
};
